<script setup lang="ts">
/**
 * CancelSubscriptionModal — 1:1 порт docs/mockups/modal-cancel-sub.html
 * (inbox-190).
 *
 * 3-шаговый мастер:
 *   1. Опрос — radio причины + textarea «что улучшить»
 *   2. Предложение — 4 варианта (скидка / пауза / downgrade / всё равно)
 *   3. Подтверждение — warning + список потерь + acknowledgment checkbox
 *
 * Backend pending: эндпоинт /api/v1/billing/cancel — в роадмапе вместе с
 * Юкасса/Тинькофф интеграцией. Сейчас submit на финальном шаге эмитит
 * 'cancel-subscription' через modal store + закрывает модалку; интегрант
 * (SettingsBillingView) дальше показывает toast «Заявка принята».
 */

import { computed, ref } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';

const modals = useModalStore();

type Step = 1 | 2 | 3;
type Reason = 'price' | 'missing' | 'competitor' | 'rare' | 'bugs' | 'other';
type Offer = 'discount' | 'pause' | 'downgrade' | 'none';

const step = ref<Step>(1);
const reason = ref<Reason>('price');
const comment = ref('');
const offer = ref<Offer>('discount');
const ack = ref(false);

interface ReasonOpt { v: Reason; title: string; desc: string; }
const REASONS_LIST: ReasonOpt[] = [
  { v: 'price',      title: 'Слишком дорого',                desc: 'Тариф не оправдывает себя для нашего объёма.' },
  { v: 'missing',    title: 'Не хватает функций',            desc: 'Нужны фичи, которых сейчас нет в продукте.' },
  { v: 'competitor', title: 'Перешли на другой сервис',       desc: 'Выбрали конкурента — Bitly, TinyURL, кастомное решение и т. п.' },
  { v: 'rare',       title: 'Редко пользуемся',              desc: 'Сейчас ссылки и метрики не приоритет.' },
  { v: 'bugs',       title: 'Ошибки или сложный интерфейс',  desc: 'Сталкиваемся с проблемами или не разобрались.' },
  { v: 'other',      title: 'Другая причина',                desc: 'Опишем подробнее в комментарии ниже.' },
];

interface OfferOpt {
  v: Offer;
  emoji: string;
  title: string;
  desc: string;
  save?: string;
  saveStrong?: string;
  highlight?: boolean;
  cancelAnyway?: boolean;
}
const OFFERS: OfferOpt[] = [
  {
    v: 'discount', emoji: '%',
    title: 'Скидка 50% на 3 месяца',
    desc: 'Останьтесь на тарифе Pro — за 7 450 ₽/мес вместо 14 900 ₽.',
    save: 'Сэкономите ', saveStrong: '22 350 ₽',
    highlight: true,
  },
  {
    v: 'pause', emoji: 'II',
    title: 'Пауза до 3 месяцев',
    desc: 'Списания остановятся, ссылки продолжат работать. Кабинет в read-only.',
    save: 'Без потери данных и истории',
  },
  {
    v: 'downgrade', emoji: '↓',
    title: 'Перейти на Standard',
    desc: '2 490 ₽/мес — до 500 ссылок, без AI-копайлота и custom domains.',
    save: 'Дешевле на ', saveStrong: '12 410 ₽/мес',
  },
  {
    v: 'none', emoji: '×',
    title: 'Всё равно отменить',
    desc: 'Перейти к шагу подтверждения. Подписка останется активной до конца оплаченного периода.',
    cancelAnyway: true,
  },
];

const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'Опрос' },
  { n: 2, label: 'Предложение' },
  { n: 3, label: 'Подтверждение' },
];
const TITLES: Record<Step, { t: string; s: string }> = {
  1: { t: 'Отмена подписки',        s: 'Расскажите, почему уходите — нам важно понять, что улучшить.' },
  2: { t: 'Прежде чем уйти',         s: 'У нас есть варианты, которые часто подходят больше, чем полная отмена.' },
  3: { t: 'Подтверждение отмены',    s: 'Последний шаг. После этого подписка будет отменена.' },
};

const title = computed(() => TITLES[step.value].t);
const subtitle = computed(() => TITLES[step.value].s);

const nextLabel = computed(() => {
  if (step.value === 3) return 'Отменить подписку';
  if (step.value === 2) return 'Продолжить отмену';
  return 'Продолжить';
});
const nextDisabled = computed(() => step.value === 3 && !ack.value);

function next(): void {
  if (step.value < 3) {
    step.value = (step.value + 1) as Step;
    return;
  }
  modals.emit('cancel-subscription', {
    reason: reason.value,
    comment: comment.value,
    offer: offer.value,
  });
  modals.close();
}
function back(): void {
  if (step.value > 1) step.value = (step.value - 1) as Step;
}
function close(): void {
  modals.close();
}
</script>

<template>
  <UiModal :title="title" :subtitle="subtitle" size="md" @close="close">
    <template #header>
      <div class="head-stack">
        <h2 class="modal-title">{{ title }}</h2>
        <p class="modal-sub">{{ subtitle }}</p>
        <div class="wiz-stepper" role="progressbar" :aria-valuenow="step" aria-valuemin="1" aria-valuemax="3">
          <template v-for="(s, i) in STEPS" :key="s.n">
            <div
              :class="[
                'step-dot',
                { current: step === s.n, done: step > s.n },
              ]"
            >
              <span>{{ s.n }}</span>
              <small>{{ s.label }}</small>
            </div>
            <div
              v-if="i < STEPS.length - 1"
              :class="['step-bar', { done: step > s.n }]"
            />
          </template>
        </div>
      </div>
    </template>

    <!-- Step 1: reasons -->
    <section v-if="step === 1" class="step">
      <div class="field">
        <label class="field-label">Почему отменяете подписку?</label>
        <div class="reason-list">
          <label
            v-for="r in REASONS_LIST"
            :key="r.v"
            class="reason-row"
            :class="{ selected: reason === r.v }"
          >
            <input type="radio" name="reason" :value="r.v" v-model="reason" />
            <div class="reason-text">
              <span class="reason-title">{{ r.title }}</span>
              <span class="reason-desc">{{ r.desc }}</span>
            </div>
          </label>
        </div>
      </div>

      <div class="field">
        <label class="field-label" for="cancel-comment">
          Что мы могли бы сделать лучше? <span class="optional">— опционально</span>
        </label>
        <textarea
          id="cancel-comment"
          v-model="comment"
          class="textarea"
          rows="3"
          placeholder="Опишите проблему, ожидание или то, что вам не подошло. Мы прочитаем — это попадает напрямую в команду продукта."
        />
      </div>
    </section>

    <!-- Step 2: offers -->
    <section v-else-if="step === 2" class="step">
      <div class="offer-hero">
        <div class="offer-icon">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M20 7h-9M14 17H5" />
            <circle cx="17" cy="17" r="3" />
            <circle cx="7" cy="7" r="3" />
          </svg>
        </div>
        <div>
          <h3 class="offer-title">Прежде чем уйти — попробуйте одно из этих</h3>
          <p class="offer-sub">
            На основе ваших данных (тариф Pro · 1 240 ссылок · 14 218 webhooks)
            мы подобрали 3 варианта вместо полной отмены.
          </p>
        </div>
      </div>

      <div class="offer-grid">
        <label
          v-for="o in OFFERS"
          :key="o.v"
          :class="[
            'offer-card',
            { selected: offer === o.v, highlight: o.highlight, 'cancel-anyway': o.cancelAnyway },
          ]"
        >
          <input type="radio" name="offer" :value="o.v" v-model="offer" />
          <div v-if="o.highlight" class="offer-tag">Рекомендуем</div>
          <div class="offer-head">
            <span class="offer-emoji" aria-hidden="true">{{ o.emoji }}</span>
            <div>
              <div class="offer-h">{{ o.title }}</div>
              <div class="offer-d">{{ o.desc }}</div>
            </div>
          </div>
          <div v-if="o.save" class="offer-save">
            {{ o.save }}<strong v-if="o.saveStrong">{{ o.saveStrong }}</strong>
          </div>
        </label>
      </div>
    </section>

    <!-- Step 3: confirmation -->
    <section v-else class="step">
      <div class="confirm-warning">
        <div class="warn-icon">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div class="warn-body">
          <strong>Подписка отменится 14 июня 2026.</strong>
          <span>До этой даты вы продолжите пользоваться тарифом Pro как обычно. После — кабинет перейдёт в режим Free.</span>
        </div>
      </div>

      <div class="loss-list">
        <h4>Что вы потеряете после 14 июня:</h4>
        <ul>
          <li>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
            <span><strong>740 ссылок</strong> сверх лимита Free — переведутся в read-only, редиректы остановятся.</span>
          </li>
          <li>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
            <span>Свои домены — <code>link.helmix.app</code> и <code>promo.helmix.app</code> отвяжутся.</span>
          </li>
          <li>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
            <span><strong>3</strong> подписки на webhooks и <strong>4</strong> API-ключа будут отозваны.</span>
          </li>
          <li>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
            <span>AI-копайлот, кастомные дашборды и расширенный антифрод.</span>
          </li>
          <li class="keep">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Всё, что укладывается в лимиты Free, продолжит работать — статистика сохранится.</span>
          </li>
        </ul>
      </div>

      <label class="confirm-checkbox">
        <input v-model="ack" type="checkbox" />
        <span>Я понимаю последствия и хочу отменить подписку.</span>
      </label>
    </section>

    <template #footer>
      <UiButton v-if="step > 1" variant="ghost" size="md" @click="back">Назад</UiButton>
      <span class="foot-spacer" />
      <UiButton v-if="step < 3" variant="ghost" size="md" @click="close">Передумал</UiButton>
      <UiButton
        :variant="step === 3 ? 'danger' : 'primary'"
        size="md"
        :disabled="nextDisabled"
        @click="next"
      >
        {{ nextLabel }}
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
/* ── 640px ширина по мокапу (md=720, поджимаем) ── */
:deep(.modal-card[data-size='md']) {
  max-width: 640px;
}

.head-stack { width: 100%; }
.modal-title {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0 0 4px;
  color: var(--fg);
}
.modal-sub {
  font-size: 13px;
  color: var(--fg-muted);
  line-height: 1.45;
  margin: 0;
}

/* ── Stepper ── */
.wiz-stepper {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 14px 0 0;
  border-top: 1px solid var(--border);
}
.step-dot {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--fg-dim);
  font-size: 12px;
  transition: 0.15s;
}
.step-dot span {
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--bg-card);
  border: 1px solid var(--border);
  font-weight: 700;
  font-size: 11.5px;
  flex-shrink: 0;
}
.step-dot small { font-weight: 500; }
.step-dot.current { color: var(--accent-hi); }
.step-dot.current span {
  background: var(--accent);
  color: #fff;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-soft);
}
.step-dot.done { color: var(--fg-muted); }
.step-dot.done span {
  background: var(--success-soft);
  color: var(--success);
  border-color: var(--success-soft);
}
.step-bar { flex: 1; height: 1px; background: var(--border); }
.step-bar.done { background: var(--success); }

/* ── Body / step container ── */
.step {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.field-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--fg);
}
.field-label .optional { color: var(--fg-dim); font-weight: 400; }
.textarea {
  resize: vertical;
  min-height: 70px;
  font-family: inherit;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 7px;
  padding: 10px 12px;
  color: var(--fg);
  font-size: 13.5px;
  line-height: 1.5;
  outline: none;
  transition: border-color 0.12s;
}
.textarea:focus { border-color: var(--accent); }
.textarea::placeholder { color: var(--fg-dim); }

/* ── Step 1: reasons ── */
.reason-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px;
}
.reason-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.1s;
}
.reason-row:hover { background: var(--bg); }
.reason-row.selected { background: var(--bg); }
.reason-row input {
  margin-top: 3px;
  accent-color: var(--accent);
  flex-shrink: 0;
}
.reason-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.reason-title { font-size: 13.5px; font-weight: 600; color: var(--fg); }
.reason-desc { font-size: 12px; color: var(--fg-muted); line-height: 1.4; }

/* ── Step 2: offers ── */
.offer-hero {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  background: var(--accent-soft);
  border-radius: 10px;
}
.offer-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--bg-elev);
  color: var(--accent);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.offer-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 4px;
  color: var(--fg);
}
.offer-sub {
  font-size: 12.5px;
  color: var(--fg-muted);
  line-height: 1.45;
  margin: 0;
}

.offer-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.offer-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 10px;
  cursor: pointer;
  transition: 0.1s;
  position: relative;
}
.offer-card input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.offer-card:hover { border-color: var(--accent); }
.offer-card.selected { border-color: var(--accent); background: var(--accent-soft); }
.offer-card.highlight { border-color: rgba(62, 207, 142, 0.4); }
.offer-card.highlight.selected {
  border-color: var(--success);
  background: rgba(62, 207, 142, 0.08);
}
.offer-card.cancel-anyway {
  border-style: dashed;
  background: transparent;
  color: var(--fg-muted);
}
.offer-card.cancel-anyway.selected {
  color: var(--fg);
  border-color: var(--danger);
  background: transparent;
}
.offer-tag {
  position: absolute;
  top: 10px;
  right: 12px;
  padding: 2px 8px;
  font-size: 10.5px;
  font-weight: 700;
  background: var(--success);
  color: #fff;
  border-radius: 100px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.offer-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.offer-emoji {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--bg-elev);
  color: var(--accent-hi);
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 15px;
  flex-shrink: 0;
}
.offer-h { font-size: 14px; font-weight: 600; margin: 0 0 2px; color: var(--fg); }
.offer-d { font-size: 12.5px; color: var(--fg-muted); line-height: 1.45; }
.offer-save {
  font-size: 12px;
  color: var(--success);
  padding: 4px 10px;
  background: rgba(62, 207, 142, 0.12);
  border-radius: 6px;
  display: inline-block;
  align-self: flex-start;
}
.offer-save strong { font-weight: 700; }

/* ── Step 3: confirmation ── */
.confirm-warning {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: var(--warning-soft);
  border: 1px solid rgba(245, 165, 36, 0.3);
  border-radius: 10px;
}
.warn-icon {
  color: var(--warning);
  flex-shrink: 0;
  margin-top: 2px;
}
.warn-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  line-height: 1.5;
}
.warn-body strong { color: var(--fg); font-weight: 600; }
.warn-body span { color: var(--fg-muted); }

.loss-list h4 {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fg-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 10px;
}
.loss-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.loss-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: var(--fg-muted);
  line-height: 1.5;
}
.loss-list li svg { color: var(--danger); margin-top: 4px; flex-shrink: 0; }
.loss-list li strong { color: var(--fg); font-weight: 600; }
.loss-list li code {
  font-family: 'SF Mono', Menlo, monospace;
  background: var(--bg-card);
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
  color: var(--fg);
}
.loss-list li.keep { color: var(--success); }
.loss-list li.keep svg { color: var(--success); }

.confirm-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: var(--fg);
}
.confirm-checkbox input {
  accent-color: var(--danger);
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* ── Footer layout ── */
.foot-spacer { flex: 1; }
</style>
